<template>
  <div id="details">
    <ks-header :back-target="backRoute">
      <template class="d-flex" #right>
        <b-button pill class="labeled-button mr-1" @click="onFavoriteClick">
          <icon-base
            :title="$t('navbar.saveAsFavorite')"
            color="#003064"
            width="18"
            height="18"
            class="m-auto"
            role="img"
          >
            <icon-favorite :filled="institution.isFavorite" />
          </icon-base>
          <label class="labeled-button-label">{{ $t("details.favorite") }}</label>
        </b-button>
        <navigator-share
          :url="sharingUrl"
          :title="sharingTitle"
          :text="$t('details.shareSubject')"
        />
      </template>
    </ks-header>

    <div id="main-container" v-scroll-lock="true">
      <div id="main-content" class="position-relative">
        <skeleton-screen v-if="loading" />
        <template v-show="!loading">
          <b-row class="mx-0 mt-0">
            <img
              id="banner"
              class="p-0"
              :srcset="`${institution.image.resourceRepresentation[1].link} 482w,
                ${institution.image.resourceRepresentation[2].link} 1031w`"
              sizes="(max-width: 482px) 482px,
                      1031px"
              :src="institution.image.resourceRepresentation[0].link"
              :alt="$t('details.banner')"
              v-if="institution.image"
            >
          </b-row>
          <template v-if="!loading">
            <b-container class="w-md-90 px-4 pt-4 pb-3">
              <!-- Headline -->
              <div class="headline text-primary">
                <h2 class="mb-1">{{ institution.name }}</h2>
                <p id="claim" v-if="institution.claim">
                  {{ institution.claim }}
                </p>
              </div>
              <b-row>
                <!-- Description -->
                <b-col>
                  <p class="m-0">{{ institution.description }}</p>
                </b-col>
                
                <!-- MuseumsCard MD+ -->
                <b-col
                  cols="12"
                  md="5"
                  v-if="institution.tags && institution.tags.includes('MuseumsCard')"
                  class="d-none d-md-block"
                >
                  <hr class="mb-4 mt-5 d-md-none">
                  <museums-card class="mt-5 mt-md-0" />
                </b-col>
              </b-row>
            </b-container>
            <!-- Living Images -->
            <section
              class="w-100"
              id="living-images-info"
              v-if="institution.hasLivingImages"
            >
              <b-container class="p-4 my-4">
                <b-row wrap>
                  <b-col md="7" class="mb-3 mb-md-0">
                    <h3>{{ $t('details.livingImagesTitle') }}</h3>
                    <p class="mb-md-0">{{ $t('details.livingImagesDescription') }}</p>
                  </b-col>
                  <b-col md="5" class="d-flex align-items-center justify-content-center justify-content-md-right">
                    <b-button
                      id="li-button"
                      class="px-4"
                      :to="`/${$route.params.locale}/institutions/${listType}/details/${id}/living-images`"
                      :disabled="!detectRTC.isGetUserMediaSupported"
                    >
                      {{ $t('details.livingImagesCallToAction') }}
                      <img
                        :alt="$t('dashboard.living-images')"
                        class="li-image ml-2"
                        src="@/assets/images/icons/livingP.svg"
                      >
                    </b-button>
                  </b-col>
                </b-row>
                <b-row v-if="!detectRTC.isGetUserMediaSupported">
                  <b-col class="mt-4 mt-md-3">
                    <b-alert
                      show
                      variant="danger"
                      id="corona-warning"
                    >
                      <b-row>
                        <b-col class="col-auto pr-0">
                          <icon-base width="20" height="20">
                            <icon-error />
                          </icon-base>
                        </b-col>
                        <b-col>
                          <span v-if="isIOSWebAppWrongVersion">{{ $t('details.livingImagesIOSWebAppWrongVersion') }}</span>
                          <span v-else-if="isIOSWebView">{{ $t('details.livingImagesIOSWebViewWarning') }}</span>
                          <span v-else>{{ $t('details.livingImagesNotSupported') }}</span>
                        </b-col>
                      </b-row>
                    </b-alert>
                  </b-col>
                </b-row>
              </b-container>
            </section>
            <hr v-else class="my-4">
            <b-container class="px-4">
              <!-- Navigation -->
              <section id="navigation" class="py-3">
                <div class="row p-0 m-0 justify-content-between">
                  <!-- Show on Map -->
                  <info-detail :img-alt="$t('common.map')">
                    <template #icon>
                      <icon-pin />
                    </template>
                    <template #text>
                      <router-link :to="`/${$route.params.locale}/institutions/${listType}/details/${id}/map`">
                        {{ $t('details.showOnMap') }}
                      </router-link>
                    </template>
                  </info-detail>
                  <!-- Drive-To -->
                  <info-detail :img-alt="$t('common.route')" v-if="institution.address">
                    <template #icon>
                      <icon-navigation />
                    </template>
                    <template #text>
                      <a
                        :href="getGoogleMapsLink(institution.address.street, institution.address.place)"
                        target="_blank"
                      >
                        {{ $t('details.routeViaNavigationApp') }}
                      </a>
                    </template>
                  </info-detail>
                  <!-- Connection NAH.SH -->
                  <nah-sh-link
                    v-if="hasNahShLink"
                    :start-pos="userPosition"
                    :end-pos="institution.position"
                    :end-name="institution.name"
                  />
                </div>
              </section>
              <hr class="mb-4">
              <!-- Contact -->
              <section id="contact" class="py-3">
                <h3 class="mb-3">{{ $t('common.contact') }}</h3>
                <div class="row m-0 p-0 justify-content-between">
                  <!-- Address -->
                  <info-detail v-if="institution.address" :img-alt="$t('details.address')">
                    <template #icon>
                      <icon-address />
                    </template>
                    <template #text>
                      <div class="text-dark">
                        {{ institution.address.street }}<br>
                        {{ institution.address.zip }} {{ institution.address.place }}
                      </div>
                    </template>
                  </info-detail>
                  <!-- Website -->
                  <info-detail
                    v-if="institution.website && institution.website.label"
                    :img-alt="$t('common.website')"
                  >
                    <template #icon>
                      <icon-globe />
                    </template>
                    <template #text>
                      <a :href="institution.website.identifier" target="_blank">
                        {{ institution.website.label }}
                      </a>
                    </template>
                  </info-detail>
                  <!-- E-Mail -->
                  <info-detail v-if="institution.email" :img-alt="$t('common.email')">
                    <template #icon>
                      <icon-mail />
                    </template>
                    <template #text>
                      <a :href="'mailto:' + institution.email">{{ institution.email }}</a>
                    </template>
                  </info-detail>
                  <!-- Telephone -->
                  <info-detail v-if="institution.tel" :img-alt="$t('details.telephone')">
                    <template #icon>
                      <icon-phone />
                    </template>
                    <template #text>
                      <a
                        :href="'tel:' + institution.tel"
                        v-if="institution.tel.length > 6 && institution.tel.length <= 16"
                      >
                        {{ institution.tel }}
                      </a>
                      <!-- fallback without link if no valid tel format -->
                      <span v-else>{{ institution.tel }}</span>
                    </template>
                  </info-detail>
                </div>
              </section>
              <hr class="mb-4">
              <!-- MuseumsCard XS & SM -->
              <section
                id="museumscard"
                class="d-md-none"
                v-if="institution.tags && institution.tags.includes('MuseumsCard')"
              >
                <museums-card class="mt-5 mt-md-0" />
                <hr class="mb-4 mt-5">
              </section>
              <!-- Opening Times -->
              <section
                id="opening-times"
                class="py-3"
              >
                <h3>{{ $t('details.openingHours') }}</h3>
                <p v-if="!institution.openingTimes">
                  {{ $t('details.noOpeningHours') }}
                </p>

                <div v-else id="opening-hours-container">
                  <div id="opening-hours-list" v-if="institution.openingTimes.week">
                    <div :class="{'opening-hours-row': true, 'opening-hours-row-active': compareDay(1)}">
                      <div class="opening-hours-day">
                        {{ $t('details.monday') }}
                      </div>
                      <div class="opening-hours-times">
                        <div v-if="institution.openingTimes.week.mon && institution.openingTimes.week.mon.first && institution.openingTimes.week.mon.first.timeStart && institution.openingTimes.week.mon.first.timeEnd">
                          <span>{{ institution.openingTimes.week.mon.first.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.mon.first.timeEnd | time($i18n.locale) }} </span>
                          <span v-if="institution.openingTimes.week.mon.second && institution.openingTimes.week.mon.second.timeStart && institution.openingTimes.week.mon.second.timeEnd">
                            <br>
                            {{ institution.openingTimes.week.mon.second.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.mon.second.timeEnd | time($i18n.locale) }} </span>
                        </div>
                        <span v-else class="opening-hours-closed-text">{{ $t('details.closed') }}</span>
                      </div>
                    </div>
                    <div :class="{'opening-hours-row': true, 'opening-hours-row-active': compareDay(2)}">
                      <div class="opening-hours-day">
                        {{ $t('details.tuesday') }}
                      </div>
                      <div class="opening-hours-times">
                        <div v-if="institution.openingTimes.week.tue && institution.openingTimes.week.tue.first && institution.openingTimes.week.tue.first.timeStart && institution.openingTimes.week.tue.first.timeEnd">
                          <span>{{ institution.openingTimes.week.tue.first.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.tue.first.timeEnd | time($i18n.locale) }} </span>
                          <span v-if="institution.openingTimes.week.tue.second && institution.openingTimes.week.tue.second.timeStart && institution.openingTimes.week.tue.second.timeEnd">
                            <br>
                            {{ institution.openingTimes.week.tue.second.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.tue.second.timeEnd | time($i18n.locale) }} </span>
                        </div>
                        <span v-else class="opening-hours-closed-text">{{ $t('details.closed') }}</span>
                      </div>
                    </div>
                    <div :class="{'opening-hours-row': true, 'opening-hours-row-active': compareDay(3)}">
                      <div class="opening-hours-day">
                        {{ $t('details.wednesday') }}
                      </div>
                      <div class="opening-hours-times">
                        <div v-if="institution.openingTimes.week.wen && institution.openingTimes.week.wen.first && institution.openingTimes.week.wen.first.timeStart && institution.openingTimes.week.wen.first.timeEnd">
                          <span>{{ institution.openingTimes.week.wen.first.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.wen.first.timeEnd | time($i18n.locale) }} </span>
                          <span v-if="institution.openingTimes.week.wen.second && institution.openingTimes.week.wen.second.timeStart && institution.openingTimes.week.wen.second.timeEnd">
                            <br>
                            {{ institution.openingTimes.week.wen.second.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.wen.second.timeEnd | time($i18n.locale) }} </span>
                        </div>
                        <span v-else class="opening-hours-closed-text">{{ $t('details.closed') }}</span>
                      </div>
                    </div>
                    <div :class="{'opening-hours-row': true, 'opening-hours-row-active': compareDay(4)}">
                      <div class="opening-hours-day">
                        {{ $t('details.thursday') }}
                      </div>
                      <div class="opening-hours-times">
                        <div v-if="institution.openingTimes.week.thu && institution.openingTimes.week.thu.first && institution.openingTimes.week.thu.first.timeStart && institution.openingTimes.week.thu.first.timeEnd">
                          <span>{{ institution.openingTimes.week.thu.first.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.thu.first.timeEnd | time($i18n.locale) }} </span>
                          <span v-if="institution.openingTimes.week.thu.second && institution.openingTimes.week.thu.second.timeStart && institution.openingTimes.week.thu.second.timeEnd">
                            <br>
                            {{ institution.openingTimes.week.thu.second.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.thu.second.timeEnd | time($i18n.locale) }} </span>
                        </div>
                        <span v-else class="opening-hours-closed-text">{{ $t('details.closed') }}</span>
                      </div>
                    </div>
                    <div :class="{'opening-hours-row': true, 'opening-hours-row-active': compareDay(5)}">
                      <div class="opening-hours-day">
                        {{ $t('details.friday') }}
                      </div>
                      <div class="opening-hours-times">
                        <div v-if="institution.openingTimes.week.fri && institution.openingTimes.week.fri.first && institution.openingTimes.week.fri.first.timeStart && institution.openingTimes.week.fri.first.timeEnd">
                          <span>{{ institution.openingTimes.week.fri.first.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.fri.first.timeEnd | time($i18n.locale) }} </span>
                          <span v-if="institution.openingTimes.week.fri.second && institution.openingTimes.week.fri.second.timeStart && institution.openingTimes.week.fri.second.timeEnd">
                            <br>
                            {{ institution.openingTimes.week.fri.second.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.fri.second.timeEnd | time($i18n.locale) }} </span>
                        </div>
                        <span v-else class="opening-hours-closed-text">{{ $t('details.closed') }}</span>
                      </div>
                    </div>
                    <div :class="{'opening-hours-row': true, 'opening-hours-row-active': compareDay(6)}">
                      <div class="opening-hours-day">
                        {{ $t('details.saturday') }}
                      </div>
                      <div class="opening-hours-times">
                        <div v-if="institution.openingTimes.week.sat && institution.openingTimes.week.sat.first && institution.openingTimes.week.sat.first.timeStart && institution.openingTimes.week.sat.first.timeEnd">
                          <span>{{ institution.openingTimes.week.sat.first.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.sat.first.timeEnd | time($i18n.locale) }} </span>
                          <span v-if="institution.openingTimes.week.sat.second && institution.openingTimes.week.sat.second.timeStart && institution.openingTimes.week.sat.second.timeEnd">
                            <br>
                            {{ institution.openingTimes.week.sat.second.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.sat.second.timeEnd | time($i18n.locale) }} </span>
                        </div>
                        <span v-else class="opening-hours-closed-text">{{ $t('details.closed') }}</span>
                      </div>
                    </div>
                    <div :class="{'opening-hours-row': true, 'opening-hours-row-active': compareDay(0)}">
                      <div class="opening-hours-day">
                        {{ $t('details.sunday') }}
                      </div>
                      <div class="opening-hours-times">
                        <div v-if="institution.openingTimes.week.sun && institution.openingTimes.week.sun.first && institution.openingTimes.week.sun.first.timeStart && institution.openingTimes.week.sun.first.timeEnd">
                          <span>{{ institution.openingTimes.week.sun.first.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.sun.first.timeEnd | time($i18n.locale) }} </span>
                          <span v-if="institution.openingTimes.week.sun.second && institution.openingTimes.week.sun.second.timeStart && institution.openingTimes.week.sun.second.timeEnd">
                            <br>
                            {{ institution.openingTimes.week.sun.second.timeStart | time($i18n.locale) }} {{ $t('details.until') }} {{ institution.openingTimes.week.sun.second.timeEnd | time($i18n.locale) }} </span>
                        </div>
                        <span v-else class="opening-hours-closed-text">{{ $t('details.closed') }}</span>
                      </div>
                    </div>
                  </div>
                  <p v-if="institution.closedHolidays">
                    {{ $t('details.closedHolidays') }}
                    <span v-for="(holiday, index) in institution.closedHolidays" :key="index">
                      {{ holiday }}<span v-if="index < institution.closedHolidays.length - 1">{{ $t('details.comma') }}</span>
                    </span>
                  </p>
                  <p v-if="institution.openingTimes.description">
                    {{ institution.openingTimes.description }}
                  </p>
                  <p class="info-text" v-if="institution.openingTimes.week">
                    {{ $t('details.noGuarantee') }}
                    <a v-b-modal.feedback-modal
                       href="#"
                    >{{ $t('details.feedbackWrongOpeningHoursHeadline') }}</a>
                  </p>
                  <b-alert
                    show
                    variant="warning"
                    id="corona-warning"
                  >
                    <b-row>
                      <b-col class="col-auto pr-0">
                        <icon-base width="20" height="20">
                          <icon-warning />
                        </icon-base>
                      </b-col>
                      <b-col>{{ $t('details.coronaWarningText') }}</b-col>
                    </b-row>
                  </b-alert>
                </div>
              </section>
              <hr class="mb-4">
              <!-- Social Media -->
              <section
                id="social-media"
                v-if="hasSocial"
                class="pt-4"
              >
                <h3>{{ $t('details.socialMedia') }}</h3>
                <social-links
                  :facebook="institution.facebook"
                  :twitter="institution.twitter"
                  :instagram="institution.instagram"
                  :youtube="institution.youtube"
                />
                <hr class="mb-4 mt-5">
              </section>
              <!-- Digital Services -->
              <section
                id="digital-services"
                v-if="hasDigitalServices"
                class="pt-4"
              >
                <h3>{{ $t('details.digitalContents') }}</h3>
                <digital-services :institution="institution" />
                <hr class="mb-4 mt-5">
              </section>
              <!-- Categories -->
              <section
                id="categories"
                v-if="institution.categories && institution.categories.length > 0"
                class="py-3"
              >
                <h3>{{ $t('common.categories') }}</h3>
                <div v-if="institution.categories" class="chip-container pt-2">
                  <router-link
                    v-for="(category, index) in institution.categories"
                    :key="index"
                    class="chip"
                    :class="{ active: categories.indexOf(category) !== -1 }"
                    :to="`${chipRouteBase}?categories=${encodeURIComponent(category)}`"
                  >
                    {{ category }}
                  </router-link>
                </div>
              </section>
              <!-- Services -->
              <section
                id="services"
                v-if="institution.tags && institution.tags.length > 0"
                class="py-3"
              >
                <h3>{{ $t('common.tags') }}</h3>
                <div v-if="institution.tags" class="chip-container pt-2">
                  <router-link
                    v-for="(tag, index) in institution.tags"
                    :key="index"
                    class="chip"
                    :class="{ active: tags.indexOf(tag) !== -1 }"
                    :to="`${chipRouteBase}?tags=${encodeURIComponent(tag)}`"
                  >
                    {{ tag }}
                  </router-link>
                </div>
              </section>
              <hr class="mt-4 mb-2">
              <section
                id="footer"
                class="w-100 m-0 pb-3 text-center"
              >
                <b-button
                  variant="link"
                  v-b-modal.feedback-modal
                  class="footer-text px-2 text-decoration-none"
                >
                  {{ $t("details.feedback") }}
                </b-button>
                <feedback-modal :institution-name="institution.name" />
                <!-- To Do: Fix Route to Impressum -->
                <router-link :to="`/${$route.params.locale}/institutions/${listType}/details/${id}/about`" class="footer-text px-2 ">
                  {{ $t("navbar.legalNotice") }}
                </router-link>
              </section>
            </b-container>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonScreen from '@/components/details/SkeletonScreen.vue'
import SocialLinks from '@/components/details/SocialLinks.vue'
import DigitalServices from '@/components/details/DigitalServices.vue'
import NahShLink from '@/components/details/NahShLink.vue'
import KsHeader from '@/components/layout/Header.vue'
import InfoDetail from '@/components/details/InfoDetail.vue'
import NavigatorShare from '@/components/details/NavigatorShare.vue'
import FeedbackModal from '@/components/details/FeedbackModal.vue'
import MuseumsCard from '@/components/details/MuseumsCard.vue'
import Navigation from '@/mixins/navigation'
import ScrollPosition from '@/mixins/scrollposition'
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import detectRTC from 'detectrtc'
import { lt } from 'semver'

export default {
  name: 'Details',
  data() {
    return {
      institution: {},
      loading: true,
      dataLocale: 'de'
    }
  },
  props: {
    id: {
      type: String,
      default: 'act001621'
    },
    listType: {
      type: String,
      default: 'map'
    }
  },
  computed: {
    ...mapState('filters', ['tags', 'categories']),
    ...mapGetters({
      userPosition: 'userPosition/getPosition'
    }),
    backRoute() {
      return !this.$route.params.listType
        ? `/${this.$route.params.locale}/institutions/${this.listType}`
        : ''
    },
    sharingUrl() {
      let getUrl = window.location
      let baseUrl = getUrl.protocol + '//' + getUrl.host
      let aliasPath = `/i/${this.id}`
      return baseUrl + aliasPath
    },
    sharingTitle() {
      return `${this.institution.name} im kulturfinder.sh`
    },
    hasSocial() {
      if (!this.institution) return false
      return !!(this.institution.facebook ||
          this.institution.twitter ||
          this.institution.instagram ||
          this.institution.youtube)
    },
    hasDigitalServices() {
      if (!this.institution.apps && !this.institution.digitalServices) return false
      return !!(this.institution.apps.ios.length > 0 ||
        this.institution.apps.android.length > 0 ||
        this.institution.digitalServices.length > 0)
    },
    hasNahShLink() {
      return this.institution.position
    },
    detectRTC() {
      return detectRTC
    },
    isIOSWebView() {
      // getUserMedia only works with Safari on iOS
      const ua = window.navigator.userAgent
      const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i)
      if (!iOS) return false
      const isBrave = !!(navigator.brave) || false
      const isChrome = !!ua.match(/CriOS/i)
      const isFirefox = !!ua.match(/FxiOS/i)
      return !ua.match(/Safari/i) || isBrave || isChrome || isFirefox
    },
    isIOSWebAppWrongVersion() {
      // getUserMedia only works as iOS-PWA with iOS 13.4.1 and higher
      const iOS = !!window.navigator.userAgent.match(/iPad/i) ||
        !!window.navigator.userAgent.match(/iPhone/i)
      return iOS &&
        window.matchMedia('(display-mode: standalone)').matches &&
        lt(this.detectRTC.osVersion, '13.4.1')
    },
    chipRouteBase() {
      return this.listType === 'dashboard'
        ? `/${this.$route.params.locale}/institutions/map`
        : `/${this.$route.params.locale}/institutions/${this.listType}`
    }
  },
  methods: {
    onFavoriteClick: async function () {
      await this.$store.dispatch('institutions/updateIsFavorite', this.institution)
    },
    compareDay(day) {
      // (sunday = 0, monday = 1 ... saturday = 6)
      return new Date().getDay() === day
    }
  },
  filters: {
    time(value, locale) {
      let time = moment('1970-01-01' + value).locale(locale).format('LT')
      if (locale === 'de') {
        time += ' Uhr'
      } else if (locale === 'da') {
        time = 'Kl. ' + time
      }
      return time
    }
  },
  mixins: [ScrollPosition, Navigation],
  components: {
    KsHeader,
    SkeletonScreen,
    SocialLinks,
    DigitalServices,
    NahShLink,
    InfoDetail,
    NavigatorShare,
    FeedbackModal,
    MuseumsCard
  },
  async activated() {
    // check if same id previously opened and locale not changed
    if (this.institution &&
      this.institution.id === this.id &&
      this.$i18n.locale === this.dataLocale) return

    // jump to top because it's a new id
    if (ScrollPosition && this.scrollposition) this.scrollposition = 0

    // get details data from vuex store
    this.loading = true
    this.institution = {}
    this.$store.dispatch('institutions/fetchDetails', { id: this.id, locale: this.$i18n.locale })
      .then((institution, error) => {
        if (institution) this.institution = institution
        if (institution.hasDetails === false) this.$bvModal.show('no-network-modal')
      })

    this.dataLocale = this.$i18n.locale
  },
  updated() {
    // check if banner available and wait until is has finished loading to remove skeleton screen
    if (!this.institution.name || !this.loading) return
    const banner = document.querySelector('#banner')
    if (banner && this.institution.image) {
      banner.onload = () => {
        this.loading = false
        if (this.$route.hash) setTimeout(() => { location.href = this.$route.hash }, 1)
      }
    } else {
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
input[type=submit] {
  font-size: inherit;
}

#living-images-info{
  background-color: $gray;
}
#claim {
    font-size: 1.1rem;
    font-weight: 400;
    font-style: italic;
    color: $primary;
}

.text-padding{
  padding: 1.5em;
}

.living-images-button{
  border-radius: 0.5rem !important;
}
#banner {
  width: 100%;
  min-height: 200px;
  max-height: 450px;
  object-fit: cover;
}
#li-button {
  border: 1px solid $primary;
  border-radius: 7px;
  min-width: 320px;
}
.li-image{
  width: 2.5rem;
  height: 100%;
}
#li-warning {
  font-size: 0.9rem;
}
.readonly {
  border-color: #869094;
  color: #869094;
}
#opening-hours-container{
color: $dark !important;
}
#opening-hours-list {
  margin-bottom: 1rem;
}

.opening-hours-row {
  display: flex;
  padding: 7px 10px;
}

.opening-hours-row-active {
  background-color: #d6e8fc;
  border-radius: 0.5rem;
}

.opening-hours-day {
  display: flex;
  flex-grow: 1;
  align-items: top;
}

.opening-hours-closed-text {
  color: #576165;
}

#corona-warning {
  font-size: 0.8rem;
}

.info-text {
  font-size: 0.8rem;
  color: #576165;
}

.chip-container {
  display: flex;
  flex-flow: row wrap;
  margin: -4px;
}

.chip {
  border: 1px solid $primary;
  border-radius: 20px;
  display: flex;
  margin: 4px;
  padding: 7px 18px;
  text-decoration: none;
  cursor: pointer;
  color: $primary!important;
}

.chip:active,
.chip.active {
  background-color: var(--primary);
  border: 1px solid var(--primary);
  color: $white !important;
}

.btn:focus, .btn:hover {
  background-color: #fff !important;
  box-shadow: none;
  border: 1px solid #fff;
}
.btn:active {
  background-color: rgb(247, 247, 247) !important;
}
.footer-text{
  font-size: 0.8rem;
  color: #576165;
}

@media (max-width: $breakpoint-md) {
  #li-button {
    width: 100%;
  }
}
</style>
